import React, { useState, useEffect } from 'react';
import { searchAddresses } from '../../services/hereMapsService';
import { debounce } from 'lodash';
import { ClearableInput } from '@rio-cloud/rio-uikit';
import { useIntl } from 'react-intl';

interface AddressSearchProps {
  value?: string;
  disabled?: boolean;
  onAddressSelect?: (address: any) => void;
  getAddress?: (address: Address) => void;
  placeholder?: string;
  onClear?: () => {};
}

interface Address {
  street: string;
  houseNumber: string;
  district: string;
  postalCode: string;
  city: string;
  state: string;
  country: string;
  countryCode: string;
  label?: string;
  geoLocation?: { lat: number, lng: number };
}

const AddressSearch: React.FC<AddressSearchProps> = ({
  value,
  onAddressSelect,
  placeholder,
  disabled,
  getAddress,
  onClear,
}) => {
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSelected, setIsSelected] = useState(true);

  const debouncedSearch = debounce(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      return;
    }

    setError(null);

    try {
      const results = await searchAddresses(searchQuery);
      setSuggestions(results);
    } catch (err) {
      setError(intl.formatMessage({ id: 'intl-msg:common.addressSearch.searchError' }));
      setSuggestions([]);
    }
  }, 500);

  useEffect(() => {
    if (isSelected) return;
    debouncedSearch(query);
    return () => {
      debouncedSearch.cancel();
    };
  }, [query]);

  useEffect(() => {
    if (value || value === '') {
      setQuery(value || '');
    }
  }, [value]);

  const handleAddressSelect = (address: any) => {
    setIsSelected(true);
    setQuery(address.address.label);
    setSuggestions([]);
    onAddressSelect?.(address.address.label || address.address.postalCode);
    getAddress?.({ ...address.address, geoLocation: address.position });
  };

  return <div className="relative w-full">
    <ClearableInput
      type="text"
      value={query}
      onChange={(e) => {
        setIsSelected(false);
        setQuery(e);
      }}
      placeholder={placeholder ?? intl.formatMessage({ id: 'intl-msg:common.addressSearch.placeholder' })}
      disabled={disabled}
      onClear={() => {
        onAddressSelect?.(null);
        setIsSelected(false);
        setQuery('');
        onClear?.();
      }}
    />

    {error && (
      <div className="text-red-500 text-sm mt-1">{error}</div>
    )}

    {suggestions.length > 0 && (
      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700 rounded-md shadow-lg max-h-60 overflow-auto">
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.id}
            onClick={() => handleAddressSelect(suggestion)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-100 cursor-pointer"
          >
            <div className="font-medium">{suggestion.title}</div>
            <div className="text-sm text-gray-600 dark:text-neutral-400">{suggestion.address.label}</div>
          </div>
        ))}
      </div>
    )}
  </div>
};

export default AddressSearch; 